import { useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import CopyButton from './CopyButton';
import { generatedCallbackPath, isValidCallbackPath, redirectUri, stripSlashes } from './callbackPath';

interface Props {
  scheme: string;
  initialPath: string;
  otherPaths: string[];
  identityBaseUrl: string | null | undefined;
  onChange: (path: string, valid: boolean) => void;
}

export default function CallbackPathField({ scheme, initialPath, otherPaths, identityBaseUrl, onChange }: Props) {
  const initial = stripSlashes(initialPath);
  const [generated, setGenerated] = useState(!initial || initial === generatedCallbackPath(scheme));
  const [custom, setCustom] = useState(initial);

  const path = generated ? generatedCallbackPath(scheme) : custom;
  const duplicate = !!path && otherPaths.some((x) => stripSlashes(x).toLowerCase() === path.toLowerCase());
  const error = !path
    ? generated
      ? 'Fyll inn scheme først'
      : 'Callback-stien må fylles inn'
    : !isValidCallbackPath(path)
      ? 'Bare bokstaver, tall og bindestrek er tillatt'
      : duplicate
        ? 'Callback-stien er allerede i bruk av en annen leverandør'
        : '';

  useEffect(() => {
    onChange(path, !error);
    // onChange is the parent's setter; only the value and its validity should re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, error]);

  const uri = redirectUri(identityBaseUrl, path);

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <TextField
          label="Callback-sti"
          fullWidth
          required
          value={path}
          disabled={generated}
          onChange={(event) => setCustom(event.target.value)}
          error={!!error && (!generated || !!scheme)}
          helperText={error || (generated ? 'Genereres fra scheme' : ' ')}
        />
        <FormControlLabel
          sx={{ flexShrink: 0, mt: 1 }}
          control={
            <Checkbox
              checked={!generated}
              onChange={(event) => {
                if (event.target.checked) setCustom(path);
                setGenerated(!event.target.checked);
              }}
            />
          }
          label="Overstyr"
        />
      </Stack>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          Redirect-URI (legges inn under Web i kundens app-registrering)
        </Typography>
        {uri ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {uri}
            </Typography>
            <CopyButton value={uri} label="Kopier redirect-URI" />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </Box>
    </Box>
  );
}
